import { Stack, Typography } from "@mui/material";
import format from "date-fns/format";
import { enUS } from "date-fns/locale";
import { useMemo } from "react";
import { MessageType } from "src/api/types";
import { usePatient } from "src/context/PatientProvider";
import { usePatientLocale } from "src/hooks/usePatientLocale";
import { parseMessage } from "src/utils/parseMessage";

interface Props {
  message: MessageType;
}

type Properties = {
  showName: boolean;
  backgroundColor: string;
  position: React.CSSProperties["alignItems"];
  color: string;
  textColor: string;
  textAlign: React.CSSProperties["textAlign"];
};

const Message = ({ message }: Props) => {
  const profileId = usePatient((patient) => patient.profile_id);
  const locale = usePatientLocale();

  const properties: Properties = useMemo(() => {
    if (message.sender.id === profileId) {
      return {
        showName: false,
        backgroundColor: "#00B6B9",
        position: "flex-end",
        color: "#91E7E8",
        textColor: "#fff",
        textAlign: "right",
      };
    }

    return {
      showName: true,
      backgroundColor: "#CCF6F7",
      color: "#82BEC0",
      position: "flex-start",
      textColor: "#000",
      textAlign: "left",
    };
  }, [message.sender.id, profileId]);

  const formattedDate = useMemo(() => {
    const enFormat = "MMM d, yyyy 'at' h:mm a";
    const esFormat = "d MMM. yyyy 'a las' h:mm a";

    return format(
      new Date(message.created_at),
      locale === enUS ? enFormat : esFormat,
      {
        locale,
      }
    );
  }, [locale, message.created_at]);

  const name = useMemo(() => {
    if (message.sender.preferred_name) {
      return message.sender.preferred_name;
    }
    return message.sender.first_name + " " + message.sender.last_name;
  }, [
    message.sender.first_name,
    message.sender.last_name,
    message.sender.preferred_name,
  ]);

  return (
    <Stack role="listitem" sx={{ whiteSpace: "pre-wrap" }}>
      {properties.showName ? (
        <Typography color={properties.color} ml={1} variant="body2">
          {name}
        </Typography>
      ) : null}
      <Stack
        alignSelf={properties.position}
        p={2}
        pt={1}
        gap={0.5}
        sx={{
          backgroundColor: properties.backgroundColor,
          borderRadius: "10px",
          minWidth: "10%",
          maxWidth: "60%",
          mb: 1,
          wordWrap: "break-word",
        }}
      >
        <Typography
          role="article"
          aria-label="Message text"
          color={properties.textColor}
          textAlign={properties.textAlign}
        >
          {parseMessage(message.message)}
        </Typography>
        <Typography
          variant="subtitle2"
          color={properties.color}
          textAlign={properties.textAlign}
          fontSize="0.7rem"
        >
          {formattedDate}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default Message;
